import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import axios from "../../axios";
import { ResetApiState, ResponseWrapper } from "../../redux.type";
import { Trip, TripReport, TripStateTypes, TripSummarized } from "./Trip.util";

const name = "trips";
const extraActions = createExtraActions();

const TripInitialState: TripStateTypes = {
  trips: ResetApiState([]),
  tripReport: ResetApiState({}),
  updateTrip: ResetApiState({}),
  monthTrips: ResetApiState([]),
  multipleTrips: ResetApiState([]),
  upsertMultipleTrips: ResetApiState(null),
  generateTrips: ResetApiState({}),
  updateTripAuditStatus: ResetApiState(null),
  updateTripAgentAuditStatus: ResetApiState(null),
};

/**
 * ACTIONS
 */
function createExtraActions() {
  const getTrips = createAsyncThunk<Trip[], any>(
    `/get-all-${name}`,
    async (date: any, thunkApi) =>
      axios
        .get(`/${name}/date/${date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const getTripMonth = createAsyncThunk<Trip[], any>(
    `/get-all-month-${name}`,
    async (data: any, thunkApi) =>
      axios
        .get(`/${name}/month/${data.startDate}/${data.endDate}/${data.routeId}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const getTripReport = createAsyncThunk<TripReport, any>(
    `/get-all-${name}-report`,
    async (id: any, thunkApi) =>
      axios
        .get(`/${name}/report/${id}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const updateTrip = createAsyncThunk<ResponseWrapper<any>, any>(
    `/update-trip`,
    async (data: any, thunkApi) =>
      axios
        .put(`/${name}/${data.id}`, data.data)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetUpdateTrip = createAction("/reset-update-trip");

  const getMultipleTrips = createAsyncThunk<TripSummarized[], any>(
    `/multiple-trip`,
    async ({ route_id, start_date, end_date }, thunkApi) =>
      axios
        .get(`/${name}/multiple/${route_id}/${start_date}/${end_date}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );
  const resetGetMultipleTrips = createAction("/reset-multiple-trips");

  const generateTrip = createAsyncThunk<ResponseWrapper<any>, any>(
    `/generate-trip`,
    async (data, thunkApi) =>
      axios
        .post(`/${name}/genetate-month`, data)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );
  const resetGenerateTrip = createAction("/reset-generate-trips");

  const upsertMultipleTrips = createAsyncThunk<ResponseWrapper<any>, any>(
    `/upsert-multiple-trip`,
    async (data: any, thunkApi) =>
      axios
        .post(`/${name}/price-update`, data)
        .then((response) => {
          if (response.data.success) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetUpsertMultipleTrips = createAction("/reset-upsert-multiple-trips");

  const updateTripAuditStatus = createAsyncThunk<ResponseWrapper<any>, any>(
    `/update-trip-audit-status`,
    async (data: any, thunkApi) =>
      axios
        .put(`/${name}/${data.id}/audit-status/${data.status}`)
        .then((response) => {
          if (response.data) {
            return response.data;
          } else {
            return thunkApi.rejectWithValue(response?.data);
          }
        })
        .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const updateTripAgentAuditStatus = createAsyncThunk<
    ResponseWrapper<any>,
    any
  >(`/update-trip-agent-audit-status`, async (data: any, thunkApi) =>
    axios
      .put(`/${name}/${data.id}/agent-audit-status/${data.status}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        } else {
          return thunkApi.rejectWithValue(response?.data);
        }
      })
      .catch((error) => thunkApi.rejectWithValue(error?.response?.data))
  );

  const resetUpdateTripAuditStatus = createAction("/reset-trip-audit-status");

  const resetUpdateTripAgentAuditStatus = createAction(
    "/reset-trip-agent-audit-status"
  );

  const resetStates = createAction(`/reset-states`);

  return {
    getTrips,
    getTripReport,
    updateTrip,
    resetUpdateTrip,
    getTripMonth,
    getMultipleTrips,
    resetGetMultipleTrips,
    upsertMultipleTrips,
    resetUpsertMultipleTrips,
    generateTrip,
    resetGenerateTrip,
    updateTripAuditStatus,
    resetUpdateTripAuditStatus,
    updateTripAgentAuditStatus,
    resetUpdateTripAgentAuditStatus,
    resetStates,
  };
}

/**
 * REDUCERS
 */
function createExtraReducers(builder: ActionReducerMapBuilder<TripStateTypes>) {
  return {
    ...getTripReducer(),
    ...getTripReportReducer(),
    ...getTripMonthReducer(),
    ...blockTripSeat(),
    ...resetStatesReducer(),
    ...getMultipleTripsReducer(),
    ...resetMultipleTripsReducer(),
    ...upsertMultipleTripsReducer(),
    ...resetUpsertMultipleTripsReducer(),
    ...resetUpdateTripReducer(),
    ...generateTripReducer(),
    ...updateTripAuditStatus(),
    ...resetUpdateTripAuditStatus(),
    ...updateTripAgentAuditStatus(),
    ...resetUpdateTripAgentAuditStatus(),
    ...resetGenerateTrips(),
  };

  function getTripReducer() {
    return {
      ...builder.addCase(extraActions.getTrips.pending, (state, action) => {
        state.trips = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getTrips.fulfilled, (state, action) => {
        state.trips = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.getTrips.rejected, (state, action) => {
        state.trips = {
          loading: false,
          payload: [],
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function getTripMonthReducer() {
    return {
      ...builder.addCase(extraActions.getTripMonth.pending, (state, action) => {
        state.monthTrips = {
          loading: true,
          payload: [],
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.getTripMonth.fulfilled,
        (state, action) => {
          state.monthTrips = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTripMonth.rejected,
        (state, action) => {
          state.monthTrips = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function getTripReportReducer() {
    return {
      ...builder.addCase(
        extraActions.getTripReport.pending,
        (state, action) => {
          state.tripReport = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTripReport.fulfilled,
        (state, action) => {
          state.tripReport = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getTripReport.rejected,
        (state, action) => {
          state.tripReport = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function blockTripSeat() {
    return {
      ...builder.addCase(extraActions.updateTrip.pending, (state, action) => {
        state.updateTrip = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.updateTrip.fulfilled, (state, action) => {
        state.updateTrip = {
          loading: false,
          payload: action.payload,
          successful: true,
          error: null,
        };
      }),
      ...builder.addCase(extraActions.updateTrip.rejected, (state, action) => {
        state.updateTrip = {
          loading: false,
          payload: null,
          successful: false,
          error: action.payload,
        };
      }),
    };
  }

  function generateTripReducer() {
    return {
      ...builder.addCase(extraActions.generateTrip.pending, (state, action) => {
        state.generateTrips = {
          loading: true,
          payload: null,
          successful: false,
          error: null,
        };
      }),
      ...builder.addCase(
        extraActions.generateTrip.fulfilled,
        (state, action) => {
          state.generateTrips = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.generateTrip.rejected,
        (state, action) => {
          state.generateTrips = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetUpdateTripReducer() {
    return {
      ...builder.addCase(extraActions.resetUpdateTrip, (state, action) => {
        state.updateTrip = ResetApiState({});
      }),
    };
  }

  function getMultipleTripsReducer() {
    return {
      ...builder.addCase(
        extraActions.getMultipleTrips.pending,
        (state, action) => {
          state.multipleTrips = {
            loading: true,
            payload: [],
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getMultipleTrips.fulfilled,
        (state, action) => {
          state.multipleTrips = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.getMultipleTrips.rejected,
        (state, action) => {
          state.multipleTrips = {
            loading: false,
            payload: [],
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetMultipleTripsReducer() {
    return {
      ...builder.addCase(
        extraActions.resetGetMultipleTrips,
        (state, action) => {
          state.multipleTrips = ResetApiState({});
        }
      ),
    };
  }

  function upsertMultipleTripsReducer() {
    return {
      ...builder.addCase(
        extraActions.upsertMultipleTrips.pending,
        (state, action) => {
          state.upsertMultipleTrips = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.upsertMultipleTrips.fulfilled,
        (state, action) => {
          state.upsertMultipleTrips = {
            loading: false,
            payload: action.payload.message,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.upsertMultipleTrips.rejected,
        (state, action) => {
          state.upsertMultipleTrips = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetUpsertMultipleTripsReducer() {
    return {
      ...builder.addCase(
        extraActions.resetUpsertMultipleTrips,
        (state, action) => {
          state.upsertMultipleTrips = ResetApiState(null);
        }
      ),
    };
  }

  function updateTripAuditStatus() {
    return {
      ...builder.addCase(
        extraActions.updateTripAuditStatus.pending,
        (state, action) => {
          state.updateTripAuditStatus = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateTripAuditStatus.fulfilled,
        (state, action) => {
          state.updateTripAuditStatus = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateTripAuditStatus.rejected,
        (state, action) => {
          state.updateTripAuditStatus = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function updateTripAgentAuditStatus() {
    return {
      ...builder.addCase(
        extraActions.updateTripAgentAuditStatus.pending,
        (state, action) => {
          state.updateTripAgentAuditStatus = {
            loading: true,
            payload: null,
            successful: false,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateTripAgentAuditStatus.fulfilled,
        (state, action) => {
          state.updateTripAgentAuditStatus = {
            loading: false,
            payload: action.payload,
            successful: true,
            error: null,
          };
        }
      ),
      ...builder.addCase(
        extraActions.updateTripAgentAuditStatus.rejected,
        (state, action) => {
          state.updateTripAgentAuditStatus = {
            loading: false,
            payload: null,
            successful: false,
            error: action.payload,
          };
        }
      ),
    };
  }

  function resetUpdateTripAuditStatus() {
    return {
      ...builder.addCase(
        extraActions.resetUpdateTripAuditStatus,
        (state, action) => {
          state.updateTripAuditStatus = ResetApiState(null);
        }
      ),
    };
  }

  function resetUpdateTripAgentAuditStatus() {
    return {
      ...builder.addCase(
        extraActions.resetUpdateTripAgentAuditStatus,
        (state, action) => {
          state.updateTripAgentAuditStatus = ResetApiState(null);
        }
      ),
    };
  }

  function resetStatesReducer() {
    return {
      ...builder.addCase(extraActions.resetStates, (state, action) => {
        state.trips = ResetApiState(null);
        state.updateTrip = ResetApiState({});
        state.generateTrips = ResetApiState({});
      }),
    };
  }

  function resetGenerateTrips() {
    return {
      ...builder.addCase(extraActions.resetGenerateTrip, (state, action) => {
        state.generateTrips = ResetApiState({});
      }),
    };
  }
}

const TripSlice = createSlice({
  name,
  initialState: TripInitialState,
  reducers: {},
  extraReducers: (builder) => createExtraReducers(builder),
});

export const TripActions = {
  ...TripSlice.actions,
  ...extraActions,
};
export const TripReducer = TripSlice.reducer;
